import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Flame, Sparkles, Trophy, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { fetchMyXp, levelFor, type XpSummary } from "@/lib/xp";

export default function XpPanel() {
  const { user } = useAuth();
  const [xp, setXp] = useState<XpSummary | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    fetchMyXp(user.id).then((s) => {
      if (active) setXp(s);
    });
    return () => {
      active = false;
    };
  }, [user]);

  if (!xp) {
    return (
      <Card className="border-border/50 bg-card/50 backdrop-blur">
        <CardContent className="p-5 h-[150px] animate-pulse" />
      </Card>
    );
  }

  const { current, next, progressPct } = levelFor(xp.totalXp);

  return (
    <Card className="border-border/50 bg-gradient-to-br from-primary/10 via-card to-card backdrop-blur overflow-hidden">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Your level
            </p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl">{current.emoji}</span>
              <h3 className="font-display text-xl font-bold text-foreground truncate">
                {current.name}
              </h3>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{current.blurb}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-muted-foreground">Total XP</p>
            <p className="font-display text-2xl font-bold text-primary">
              {xp.totalXp.toLocaleString()}
            </p>
          </div>
        </div>

        {next ? (
          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
              <span>
                Next: <span className="text-foreground font-semibold">{next.name}</span>
              </span>
              <span>
                {xp.totalXp - current.min}/{next.min - current.min} XP
              </span>
            </div>
            <Progress value={progressPct} className="h-2" />
          </div>
        ) : (
          <p className="text-xs text-primary font-medium">Max level reached — legend.</p>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-background/60 border border-border/50 px-3 py-2">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Flame className="h-3.5 w-3.5 text-orange-500" />
              Streak
            </div>
            <p className="font-display font-bold text-foreground mt-0.5">
              {xp.currentStreak}{" "}
              <span className="text-xs font-normal text-muted-foreground">
                day{xp.currentStreak === 1 ? "" : "s"}
              </span>
            </p>
          </div>
          <div className="rounded-lg bg-background/60 border border-border/50 px-3 py-2">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Best
            </div>
            <p className="font-display font-bold text-foreground mt-0.5">
              {xp.longestStreak}{" "}
              <span className="text-xs font-normal text-muted-foreground">
                day{xp.longestStreak === 1 ? "" : "s"}
              </span>
            </p>
          </div>
        </div>

        <Link
          to="/leaderboard"
          className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-foreground bg-background/60 hover:bg-background border border-border/50 transition-colors"
        >
          <span className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" /> View leaderboards
          </span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        </Link>
      </CardContent>
    </Card>
  );
}
